import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaDieciseisM(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // bg
      ctx.fillStyle = opts.bgColor || props.bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // layout paddings
      const padding = 24;
      let y = padding;

      // compute left/right widths early so title fits inside the left column
      const usableW = Math.max(0, width - padding * 2);
      const interGap = 8; // gap between left block and right text column
      const leftAreaW = Math.round(usableW * 0.65);
      const rightAreaW = Math.max(0, usableW - leftAreaW - interGap);
      const leftAreaX = padding;
      const rightAreaX = leftAreaX + leftAreaW + interGap;

      // draw left (gray) and right (white) column backgrounds before the title
      ctx.fillStyle = opts.leftAreaBg || '#ffffff';
      ctx.fillRect(leftAreaX, 0, leftAreaW, height);

      ctx.fillStyle = opts.rightAreaBg || '#ffffff';
      ctx.fillRect(rightAreaX, 0, rightAreaW, height);

      // title square + title (placed inside the left column with a small inner inset)
      const titleFontSize = opts.titleFontSize || 50;
      const squareSize = typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 0.9);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 18;
      const squareText = opts.titleSquareText || '2.3';
      const innerLeftPad = 8;
      const squareX = leftAreaX + innerLeftPad;

      // square (inside left column)
      ctx.fillStyle = opts.titleSquareBg || '#0ea5e9';
      ctx.fillRect(squareX, y, squareSize, squareSize);

      ctx.fillStyle = opts.titleSquareColor || '#ffffff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, squareX + squareSize / 2, y + squareSize / 2);

      // title (wrapped/justified) constrained to left column
      const title = opts.title || props.title || '';
      const titleX = squareX + squareSize + squareGap;
      const titleMaxW = Math.min(
        typeof opts.titleMaxWidth === 'number' ? opts.titleMaxWidth : 1200,
        Math.max(0, leftAreaW - (squareSize + squareGap) - innerLeftPad - 12)
      );

      ctx.fillStyle = opts.titleColor || '#111827';
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      const justifyAndWrap = (c, text, x, y0, maxW, lineH) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = [];
        let curW = 0;
        const spaceW = c.measureText(' ').width;
        for (let w of words) {
          const ww = c.measureText(w).width;
          if (cur.length === 0) {
            cur.push(w); curW = ww;
          } else if (curW + spaceW + ww <= maxW) {
            cur.push(w); curW += spaceW + ww;
          } else {
            lines.push({ words: cur.slice(), wordsWidth: curW });
            cur = [w]; curW = ww;
          }
        }
        if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });

        let yPos = y0;
        for (let i = 0; i < lines.length; i++) {
          const ln = lines[i];
          if (i < lines.length - 1 && ln.words.length > 1) {
            const gaps = ln.words.length - 1;
            const extra = (maxW - ln.wordsWidth) / gaps;
            let cx = x;
            for (let j = 0; j < ln.words.length; j++) {
              c.fillText(ln.words[j], cx, yPos);
              cx += c.measureText(ln.words[j]).width + spaceW + extra;
            }
          } else {
            c.fillText(ln.words.join(' '), x, yPos);
          }
          yPos += lineH;
        }
        return yPos;
      };

      const titleLineH = Math.round(titleFontSize * 1.2);
      const afterTitleY = justifyAndWrap(ctx, title, titleX, y, titleMaxW, titleLineH);

      // subtitle under title
      const subTitle = opts.subTitle || props.subTitle || '';
      if (subTitle) {
        const subFontSize = typeof opts.subTitleFontSize === 'number' ? opts.subTitleFontSize : 30;
        ctx.font = `${subFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.subTitleColor || '#475569';
        const subLineH = Math.round(subFontSize * 1.2);
        const afterSubY = justifyAndWrap(ctx, subTitle, titleX, afterTitleY + 8, titleMaxW, subLineH);
        y = Math.max(y + squareSize, afterSubY) + 24;
      } else {
        y = Math.max(y + squareSize, afterTitleY) + 24;
      }

      // graficaUno (pie) only — no split, no graficaDos
      const graficaUnoData = opts.graficaUnoData || props.graficaUnoData || [];

      const chartsTopGap = typeof opts.chartsTopGap === 'number' ? opts.chartsTopGap : 50;
      const chartsStartY = y + chartsTopGap;

      // compute shared area for pie and legend inside left area
      const leftContentX = leftAreaX + innerLeftPad;
      const leftContentW = Math.max(0, leftAreaW - innerLeftPad * 2);

      const computedAreaBottom = Math.max(chartsStartY + 80, height - padding - 140);

      // pie placement (centered in left area, bottom-aligned with computedAreaBottom)
      const maxPieByWidth = Math.max(0, leftContentW - 16);
      const maxPieByHeight = Math.max(0, computedAreaBottom - chartsStartY - 8);
      const pieDiameter = Math.max(0, Math.min(600, maxPieByWidth, maxPieByHeight));
      const pieR = Math.max(10, Math.round(pieDiameter / 2));
      const pieX = leftContentX + Math.round(leftContentW / 2);
      const pieY = Math.max(chartsStartY + pieR, computedAreaBottom - pieR);

      const palette = opts.palette || ['#44cdef', '#062cd4', '#76a6e6', '#10b981', '#6366f1', '#a78bfa'];
      const contrastFor = (hex) => {
        try {
          const h = hex.replace('#','');
          const r = parseInt(h.length===3 ? h[0]+h[0] : h.slice(0,2),16);
          const g = parseInt(h.length===3 ? h[1]+h[1] : h.slice(2,4),16);
          const b = parseInt(h.length===3 ? h[2]+h[2] : h.slice(4,6),16);
          const lum = 0.299*r + 0.587*g + 0.114*b;
          return lum < 140 ? '#ffffff' : '#0f172a';
        } catch (e) { return '#ffffff'; }
      };

      const totalVal = graficaUnoData.reduce((s, it) => s + (Number(it.cantidad) || 0), 0) || 1;
      let ang = -Math.PI / 2;
      for (let i = 0; i < graficaUnoData.length; i++) {
        const it = graficaUnoData[i];
        const v = Number(it.cantidad) || 0;
        const a = (v / totalVal) * Math.PI * 2;
        const startAng = ang;
        const endAng = ang + a;

        ctx.beginPath();
        ctx.moveTo(pieX, pieY);
        const fillColor = palette[i % palette.length];
        ctx.fillStyle = fillColor;
        ctx.arc(pieX, pieY, pieR, startAng, endAng);
        ctx.lineTo(pieX, pieY);
        ctx.fill();

        const mid = startAng + a / 2;
        const labelRadius = Math.max(12, Math.round(pieR * 0.62));
        const lx = pieX + Math.cos(mid) * labelRadius;
        const ly = pieY + Math.sin(mid) * labelRadius;
        const pct = Math.round((v / totalVal) * 1000) / 10;
        const labelText = (Number.isFinite(v) ? String(v) : '') + (typeof v === 'number' ? ` (${pct}%)` : '');

        ctx.save();
        ctx.font = `${Math.max(10, Math.round(pieR * 0.12))}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = contrastFor(fillColor);
        if (a > 0.12) {
          ctx.fillText(labelText, lx, ly);
        } else {
          ctx.beginPath();
          ctx.fillStyle = contrastFor(fillColor);
          ctx.arc(pieX + Math.cos(mid) * (pieR + 8), pieY + Math.sin(mid) * (pieR + 8), 3, 0, Math.PI*2);
          ctx.fill();
        }
        ctx.restore();

        ang += a;
      }

      // legend under pie (left area)
      ctx.font = `${opts.legendFontSize || 14}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      let legendY = computedAreaBottom + 8;
      const legendX = leftContentX + 8;
      for (let i = 0; i < graficaUnoData.length; i++) {
        const it = graficaUnoData[i];
        ctx.fillStyle = palette[i % palette.length];
        ctx.fillRect(legendX, legendY, 12, 12);
        ctx.fillStyle = opts.legendColor || '#111827';
        ctx.fillText(`${it.pais} (${it.cantidad})`, legendX + 18, legendY - 2);
        legendY += 18;
      }

      // bottom text under the pie legend
      const textoUno = opts.textoUno || props.textoUno || '';
      ctx.font = `${opts.bottomFontSize || 16}px Inter, Arial, sans-serif`;
      ctx.fillStyle = opts.bottomColor || '#374151';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      const drawSmallWrap = (c, text, x, y0, maxW, lh) => {
        const words = String(text).split(' ');
        let line = '';
        let yPos = y0;
        for (let n = 0; n < words.length; n++) {
          const test = line ? line + ' ' + words[n] : words[n];
          if (c.measureText(test).width > maxW && line) {
            c.fillText(line, x, yPos);
            line = words[n];
            yPos += lh;
          } else {
            line = test;
          }
        }
        if (line) c.fillText(line, x, yPos);
        return yPos + lh;
      };

      const leftTextX = legendX;
      const leftTextMaxW = leftContentW - 16;
      const leftTextY = legendY + 6;
      if (textoUno) drawSmallWrap(ctx, textoUno, leftTextX, leftTextY, leftTextMaxW, 20);

      // right column textoRight (array supported) — unchanged
      const textoRightRaw = opts.textoRight ?? opts.rightText ?? props.textoRight ?? props.rightText ?? '';
      const rightParagraphs = [];
      if (Array.isArray(textoRightRaw)) {
        for (let p of textoRightRaw) if (String(p || '').trim()) rightParagraphs.push(String(p));
      } else if (String(textoRightRaw || '').trim()) {
        rightParagraphs.push(String(textoRightRaw));
      }

      if (rightParagraphs.length) {
        ctx.save();

        const wrapLinesWithMeta = (c, text, maxW) => {
          const words = String(text).split(/\s+/).filter(Boolean);
          const lines = [];
          let cur = [];
          let curW = 0;
          const spaceW = c.measureText(' ').width;
          for (let w of words) {
            const ww = c.measureText(w).width;
            if (cur.length === 0) {
              cur.push(w); curW = ww;
            } else if (curW + spaceW + ww <= maxW) {
              cur.push(w); curW += spaceW + ww;
            } else {
              lines.push({ words: cur.slice(), wordsWidth: curW });
              cur = [w]; curW = ww;
            }
          }
          if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });
          return lines;
        };

        let rightFontSize = typeof opts.textoRightSize === 'number' ? opts.textoRightSize : 35;
        const minFontSize = 8;
        ctx.fillStyle = opts.textoRightColor || '#374151';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';

        const maxRightW = Math.max(8, rightAreaW - 24);
        const topBound = padding;
        const bottomBound = height - padding;

        let paragraphLines = [];
        let lineH = 0;
        let totalH = 0;
        while (rightFontSize >= minFontSize) {
          ctx.font = `${rightFontSize}px Inter, Arial, sans-serif`;
          paragraphLines = [];
          for (let p of rightParagraphs) {
            const wrapped = wrapLinesWithMeta(ctx, p, maxRightW);
            if (wrapped.length === 0) wrapped.push({ words: [''], wordsWidth: 0 });
            paragraphLines.push(wrapped);
          }
          const totalLines = paragraphLines.reduce((sum, para, idx) => sum + para.length + (idx < paragraphLines.length - 1 ? 1 : 0), 0);
          lineH = Math.round(rightFontSize * 1.3);
          totalH = totalLines * lineH;
          if (totalH <= (bottomBound - topBound) || rightFontSize === minFontSize) break;
          rightFontSize -= 1;
        }

        const spaceW = ctx.measureText(' ').width;
        const startY = topBound + Math.round(((bottomBound - topBound) - totalH) / 2) + Math.round(lineH / 2);
        const leftX = rightAreaX + 12;

        ctx.fillStyle = opts.textoRightColor || '#374151';
        let currentY = startY;
        for (let pIdx = 0; pIdx < paragraphLines.length; pIdx++) {
          const para = paragraphLines[pIdx];
          for (let lIdx = 0; lIdx < para.length; lIdx++) {
            const ln = para[lIdx];
            const isLastLineOfParagraph = lIdx === para.length - 1;
            if (!isLastLineOfParagraph && ln.words.length > 1) {
              // justify
              const gaps = ln.words.length - 1;
              const extra = (maxRightW - ln.wordsWidth) / gaps;
              let cx = leftX;
              for (let j = 0; j < ln.words.length; j++) {
                ctx.fillText(ln.words[j], cx, currentY);
                cx += ctx.measureText(ln.words[j]).width + spaceW + extra;
              }
            } else {
              ctx.fillText(ln.words.join(' '), leftX, currentY);
            }
            currentY += lineH;
          }
          if (pIdx < paragraphLines.length - 1) currentY += lineH; // blank line between paragraphs
        }

        ctx.restore();
      }

      // footer
      ctx.fillStyle = opts.footerColor || '#6b7280';
      ctx.font = `${opts.footerFontSize ?? 25}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'top';

      const footerInset = typeof opts.footerInset === 'number' ? opts.footerInset : 8;
      const footerMaxW = Math.max(0, rightAreaW - footerInset - 8);

      const wrapFooter = (c, text, maxW) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = '';
        for (let w of words) {
          const test = cur ? cur + ' ' + w : w;
          if (c.measureText(test).width <= maxW) {
            cur = test;
          } else {
            if (cur) lines.push(cur);
            cur = w;
          }
        }
        if (cur) lines.push(cur);
        return lines;
      };

      const footerText = opts.footerText || props.footerText || '';
      const footerLines = wrapFooter(ctx, footerText, footerMaxW);
      const footerLineH = Math.round((opts.footerLineHeight ?? 1.2) * (opts.footerFontSize ?? 25));
      const footerBlockH = footerLines.length * footerLineH;
      const defaultFooterY = height - padding - footerBlockH - 200;
      let footerY = defaultFooterY;
      footerY = Math.max(padding, Math.min(footerY, height - padding - footerBlockH));
      const footerX = rightAreaX + rightAreaW - footerInset;
      for (let i = 0; i < footerLines.length; i++) {
        ctx.fillText(footerLines[i], footerX, footerY + i * footerLineH);
      }

      // optional logo bottom-right
      const mainLogoSrc = opts.logoSrc || props.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(240, Math.round(width * 0.12));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_catorce_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});